import {
  createAsyncThunk,
  createSlice,
  current,
  PayloadAction,
} from "@reduxjs/toolkit";
import {
  getIndividualWorkflow,
  getWorkflows,
} from "../../../services/APIService";
import {
  addEdge,
  Edge,
  isNode,
  Node,
  removeElements,
} from "react-flow-renderer";
import reducerStatus from "../../../models/reducerStatus";
import Workflow from "../../../models/workflow/backend/Workflow.model";
import { RootState } from "../../store/store";

export type workflowOrientation = "horizontal" | "vertical";

export interface workflowState {
  workflowList: any[];
  status: reducerStatus;
  elements: any[];
  selectedElement: Node | Edge | null;
  selectedElementIndex: number | null;
  orientation: workflowOrientation;
  workflow: Workflow;
  isEdit: boolean;
  changeMade: boolean;
  displayEventsType: displayEventsType | null;
  retrievedSingleWorkflow: boolean;
}

export type displayEventsType = "OnStart" | "OnComplete";

const defaultWorkflow: Workflow = {
  name: "",
  type: "Projects",
};
const AddWorkflowMock: Workflow = {
  name: "AddWorkflow",
  type: "Projects",
};
const initialState: workflowState = {
  elements: [],
  selectedElement: null,
  selectedElementIndex: null,
  orientation: "vertical",
  workflow: defaultWorkflow,
  workflowList: [AddWorkflowMock],
  status: "idle",
  isEdit: false,
  changeMade: false,
  displayEventsType: null,
  retrievedSingleWorkflow: false,
};

export const getWorkflowsAsync = createAsyncThunk(
  "workflows/getWorkflows",
  async (_, { rejectWithValue }) => {
    const response = (await getWorkflows() as any);
    if (!(response.status as number).toString().startsWith("2"))
      return rejectWithValue(response.data);
    return response.data;
  }
);

export const getIndividualWorkflowAsync = createAsyncThunk(
  "workflow/getSingleWorkflow",
  async (id: number, { rejectWithValue }) => {
    const response = (await getIndividualWorkflow(id) as any);
    if (!(response.status as number).toString().startsWith("2"))
      return rejectWithValue(response.data);
    return response.data;
  }
);

export const workflowSlice = createSlice({
  name: "workflow",
  initialState,
  reducers: {
    addWorkflowEdge: (state, action: PayloadAction<any>) => {
      state.elements = addEdge(action.payload, state.elements);
      state.changeMade = true;
    },
    addWorkflowNode: (state, action: PayloadAction<Node>) => {
      state.elements = state.elements.concat(action.payload);
      state.changeMade = true;
    },
    removeWorkflowNode: (state, action: PayloadAction<(Node | Edge)[]>) => {
      state.elements = removeElements(action.payload, state.elements);
      state.changeMade = true;
    },
    setSelectedElement: (state, action: PayloadAction<any>) => {
      state.selectedElement = action.payload;
      if (action.payload) {
        const index = state.elements.findIndex(
          (element: Node | Edge) => element.id === action.payload.id
        );
        state.selectedElementIndex = index;
      } else {
        state.selectedElementIndex = null;
      }
    },
    updateElement: (state, action: PayloadAction<Node | Edge>) => {
      state.elements = state.elements.map((els) =>
        els.id === action.payload.id ? action.payload : els
      );
      state.selectedElement = action.payload;
      state.changeMade = true;
    },
    setElements: (state, action: PayloadAction<(Node | Edge)[]>) => {
      state.elements = action.payload;
    },
    setWorkflowOrientation: (
      state,
      action: PayloadAction<workflowOrientation>
    ) => {
      state.orientation = action.payload;
    },
    deleteNode: (state, action: PayloadAction<string>) => {
      const elementsToRemove = state.elements.filter((element: Node | Edge) =>
        isNode(element)
          ? element.id === action.payload.toString()
          : element.source === action.payload.toString() ||
            element.target === action.payload.toString()
      );
      state.elements = removeElements(elementsToRemove, state.elements);
      state.changeMade = true;
    },
    updateWorkflowName: (state, action: PayloadAction<string>) => {
      state.workflow.name = action.payload;
    },
    setIsEdit: (state, action: PayloadAction<boolean>) => {
      state.isEdit = action.payload;
    },
    setChangeMade: (state, action: PayloadAction<boolean>) => {
      state.changeMade = action.payload;
    },
    setWorkflowToDefault: (state) => {
      state.workflow = defaultWorkflow;
    },
    setDisplayEvent: (
      state,
      action: PayloadAction<displayEventsType | null>
    ) => {
      state.displayEventsType = action.payload;
    },
    updateWorkflowDescription: (state, action: PayloadAction<string>) => {
      state.workflow.description = action.payload;
    },
    setRetrievedSingleWorkflow: (state, action: PayloadAction<boolean>) => {
      state.retrievedSingleWorkflow = action.payload;
    },
    resetWorkflowSlice: (state, action: PayloadAction<boolean>) => {
      state.elements = [];
      state.selectedElement = null;
      state.selectedElementIndex = null;
      state.orientation = "vertical";
      state.workflow = defaultWorkflow;
      state.status = "idle";
      state.isEdit = false;
      state.changeMade = false;
      state.displayEventsType = null;
      state.retrievedSingleWorkflow = false;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getWorkflowsAsync.rejected, (state) => {
        state.status = "failed";
      })
      .addCase(getWorkflowsAsync.pending, (state) => {
        state.status = "loading";
      })
      .addCase(getWorkflowsAsync.fulfilled, (state, action) => {
        state.status = "idle";
        if (!action.payload) return;
        state.workflowList = action.payload;
        state.workflowList.unshift(AddWorkflowMock);
      })
      .addCase(getIndividualWorkflowAsync.rejected, (state) => {
        state.status = "failed";
      })
      .addCase(getIndividualWorkflowAsync.pending, (state) => {
        state.status = "loading";
      })
      .addCase(getIndividualWorkflowAsync.fulfilled, (state, action) => {
        state.status = "idle";
        if (!action.payload) return;
        state.workflow = action.payload;
        state.retrievedSingleWorkflow = true;
        state.changeMade = false;
      });
  },
});
export const {
  addWorkflowEdge,
  addWorkflowNode,
  removeWorkflowNode,
  setSelectedElement,
  updateElement,
  setElements,
  setWorkflowOrientation,
  deleteNode,
  updateWorkflowName,
  setIsEdit,
  setWorkflowToDefault,
  setDisplayEvent,
  updateWorkflowDescription,
  setRetrievedSingleWorkflow,
  resetWorkflowSlice,
} = workflowSlice.actions;

export const selectWorkflowElements = (state: RootState): (Node | Edge)[] =>
  state.workflow.elements;

export const selectCurrentElement = (state: RootState): Node | Edge | null => {
  const currentElementIndex = (state.workflow as workflowState)
    .selectedElementIndex;
  return currentElementIndex !== null
    ? (state.workflow as workflowState).elements[currentElementIndex]
    : null;
};

export const selectInputElements = (
  state: RootState
): Node | Edge | undefined =>
  (state.workflow as workflowState).elements.find((el) => el.type === "input");

export const selectCurrentElementIndex = (state: RootState): number | null =>
  (state.workflow as workflowState).selectedElementIndex;

export const selectWorkflowCurrentOrientation = (
  state: RootState
): workflowOrientation => (state.workflow as workflowState).orientation;

export const selectWorkflow = (state: RootState): Workflow =>
  (state.workflow as workflowState).workflow;

export const selectWorkflowList = (state: RootState): Workflow[] =>
  (state.workflow as workflowState).workflowList;

export const selectIsEdit = (state: RootState): boolean =>
  (state.workflow as workflowState).isEdit;

export const selectChangeMade = (state: RootState): boolean =>
  (state.workflow as workflowState).changeMade;

export const selectDisplayEvents = (
  state: RootState
): displayEventsType | null =>
  (state.workflow as workflowState).displayEventsType;

export const selectRetrievedSingleWorkflow = (state: RootState): boolean =>
  (state.workflow as workflowState).retrievedSingleWorkflow;

export default workflowSlice.reducer;
